import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for adding auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('session_token');
    if (token) {
      // Add token as query param for simple auth
      const separator = config.url.includes('?') ? '&' : '?';
      if (!config.url.includes('token=')) {
        // Don't add token to URLs that already have it
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only clear session and redirect if this is a session validation failure
      // Check if this is the session endpoint or if we're explicitly told to logout
      const isSessionEndpoint = error.config?.url?.includes('/auth/session');
      const isLoginEndpoint = error.config?.url?.includes('/auth/login');
      
      if (isSessionEndpoint || isLoginEndpoint) {
        // This is a genuine auth failure - clear session and redirect
        localStorage.removeItem('session_token');
        // Only redirect if not already on login page
        if (!window.location.pathname.includes('/login') && 
            !window.location.pathname.includes('/demo')) {
          window.location.href = '/login';
        }
      }
      // For other 401s, just reject the promise - let the component handle it
    }
    return Promise.reject(error);
  }
);

export default api;

// Helper functions for common API calls
export const apiHelpers = {
  // Organization
  getOrganization: (orgId) => api.get(`/organizations/${orgId}`),
  updateOrganization: (orgId, data, userId) => 
    api.put(`/organizations/${orgId}?user_id=${userId}`, data),
  
  // Users
  getUsers: (orgId) => api.get(`/organizations/${orgId}/users`),
  inviteUser: (orgId, data, createdByUserId) => 
    api.post(`/organizations/${orgId}/users/invite?created_by_user_id=${createdByUserId}`, data),
  updateUser: (orgId, userId, data, adminUserId) =>
    api.put(`/organizations/${orgId}/users/${userId}?admin_user_id=${adminUserId}`, data),
  deleteUser: (orgId, userId, adminUserId) =>
    api.delete(`/organizations/${orgId}/users/${userId}?admin_user_id=${adminUserId}`),
  resendInvite: (orgId, userId) => 
    api.post(`/organizations/${orgId}/users/${userId}/resend-invite`),
  deactivateUser: (orgId, userId, adminUserId) => 
    api.put(`/organizations/${orgId}/users/${userId}/deactivate?admin_user_id=${adminUserId}`),
  reactivateUser: (orgId, userId, adminUserId) => 
    api.put(`/organizations/${orgId}/users/${userId}/reactivate?admin_user_id=${adminUserId}`),
  updateUserLanguage: (orgId, userId, language) => 
    api.put(`/organizations/${orgId}/users/${userId}/language`, { preferred_language: language }),
  
  // Invite verification
  verifyInvite: (token) => api.get(`/auth/verify-invite?token=${token}`),
  acceptInvite: (token, password) => api.post('/auth/accept-invite', { token, password }),
  
  // Clients
  getClients: (orgId, search = '') => 
    api.get(`/organizations/${orgId}/clients${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  getClient: (orgId, clientId) => api.get(`/organizations/${orgId}/clients/${clientId}`),
  createClient: (orgId, data, userId, userName) => 
    api.post(`/organizations/${orgId}/clients?user_id=${userId}&user_name=${encodeURIComponent(userName)}`, data),
  updateClient: (orgId, clientId, data, userId) => 
    api.put(`/organizations/${orgId}/clients/${clientId}?user_id=${userId}`, data),
  deleteClient: (orgId, clientId, userId) => 
    api.delete(`/organizations/${orgId}/clients/${clientId}?user_id=${userId}`),
  getClientActivity: (orgId, clientId) =>
    api.get(`/organizations/${orgId}/clients/${clientId}/activity`),
  getArchivedClients: (orgId) => api.get(`/organizations/${orgId}/clients/archived`),
  restoreClient: (orgId, clientId, userId) => 
    api.put(`/organizations/${orgId}/clients/${clientId}/restore?user_id=${userId}`),
  permanentlyDeleteClient: (orgId, clientId, userId) => 
    api.delete(`/organizations/${orgId}/clients/${clientId}/permanent?user_id=${userId}`),
  
  // Estimates
  getEstimates: (orgId, status = '', clientId = '') => {
    let url = `/organizations/${orgId}/estimates`;
    const params = [];
    if (status) params.push(`status=${status}`);
    if (clientId) params.push(`client_id=${clientId}`);
    if (params.length) url += '?' + params.join('&');
    return api.get(url);
  },
  getEstimate: (orgId, estimateId) => api.get(`/organizations/${orgId}/estimates/${estimateId}`),
  createEstimate: (orgId, data, userId, userName) => 
    api.post(`/organizations/${orgId}/estimates?user_id=${userId}&user_name=${encodeURIComponent(userName)}`, data),
  updateEstimate: (orgId, estimateId, data, userId) => 
    api.put(`/organizations/${orgId}/estimates/${estimateId}?user_id=${userId}`, data),
  duplicateEstimate: (orgId, estimateId, userId, userName) => 
    api.post(`/organizations/${orgId}/estimates/${estimateId}/duplicate?user_id=${userId}&user_name=${encodeURIComponent(userName)}`),
  sendEstimate: (orgId, estimateId, userId, userName, includePdf = true) => 
    api.post(`/organizations/${orgId}/estimates/${estimateId}/send?user_id=${userId}&user_name=${encodeURIComponent(userName)}&include_pdf=${includePdf}`),
  deleteEstimate: (orgId, estimateId, userId) => 
    api.delete(`/organizations/${orgId}/estimates/${estimateId}?user_id=${userId}`),
  getEstimatePdf: (orgId, estimateId) => 
    api.get(`/organizations/${orgId}/estimates/${estimateId}/pdf`, { responseType: 'blob' }),
  
  // Estimate Review Queue (for managers/owners)
  getEstimatesPendingReview: (orgId) =>
    api.get(`/organizations/${orgId}/estimates/pending-review`),
  approveEstimate: (orgId, estimateId, userId, userName) =>
    api.post(`/organizations/${orgId}/estimates/${estimateId}/approve?user_id=${userId}&user_name=${encodeURIComponent(userName)}`),
  
  // Estimate Templates
  getEstimateTemplates: (orgId, activeOnly = true) => 
    api.get(`/organizations/${orgId}/estimate-templates?active_only=${activeOnly}`),
  getEstimateTemplate: (orgId, templateId) => 
    api.get(`/organizations/${orgId}/estimate-templates/${templateId}`),
  createEstimateTemplate: (orgId, data, userId) => 
    api.post(`/organizations/${orgId}/estimate-templates?user_id=${userId}`, data),
  updateEstimateTemplate: (orgId, templateId, data, userId) => 
    api.put(`/organizations/${orgId}/estimate-templates/${templateId}?user_id=${userId}`, data),
  deleteEstimateTemplate: (orgId, templateId, userId) => 
    api.delete(`/organizations/${orgId}/estimate-templates/${templateId}?user_id=${userId}`),
  useEstimateTemplate: (orgId, templateId) => 
    api.post(`/organizations/${orgId}/estimate-templates/${templateId}/use`),
  seedDefaultTemplates: (orgId, userId) =>
    api.post(`/organizations/${orgId}/estimate-templates/seed-defaults?user_id=${userId}`),
  
  // Split / Combine Estimates
  splitEstimateToJobs: (orgId, estimateId, groups, userId, userName) =>
    api.post(`/organizations/${orgId}/estimates/${estimateId}/split-to-jobs?user_id=${userId}&user_name=${encodeURIComponent(userName)}`, { groups }),
  splitEstimateToInvoices: (orgId, estimateId, groups, userId, userName) =>
    api.post(`/organizations/${orgId}/estimates/${estimateId}/split-to-invoices?user_id=${userId}&user_name=${encodeURIComponent(userName)}`, { groups }),
  getApprovedEstimatesForClient: (orgId, clientId) =>
    api.get(`/organizations/${orgId}/clients/${clientId}/approved-estimates`),
  combineEstimatesToJob: (orgId, data, userId, userName) =>
    api.post(`/organizations/${orgId}/estimates/combine-to-job?user_id=${userId}&user_name=${encodeURIComponent(userName)}`, data),
  combineEstimatesToInvoice: (orgId, data, userId, userName) =>
    api.post(`/organizations/${orgId}/estimates/combine-to-invoice?user_id=${userId}&user_name=${encodeURIComponent(userName)}`, data),

  // Checklist Settings & Templates
  getChecklistSettings: (orgId) => api.get(`/organizations/${orgId}/checklist-settings`),
  updateChecklistSettings: (orgId, data, userId) =>
    api.put(`/organizations/${orgId}/checklist-settings?user_id=${userId}`, data),
  getChecklistTemplates: (orgId) => api.get(`/organizations/${orgId}/checklist-templates`),
  createChecklistTemplate: (orgId, data, userId) =>
    api.post(`/organizations/${orgId}/checklist-templates?user_id=${userId}`, data),
  updateChecklistTemplate: (orgId, templateId, data, userId) =>
    api.put(`/organizations/${orgId}/checklist-templates/${templateId}?user_id=${userId}`, data),
  deleteChecklistTemplate: (orgId, templateId, userId) =>
    api.delete(`/organizations/${orgId}/checklist-templates/${templateId}?user_id=${userId}`),

  // Leads
  getLeads: (orgId, status = '') =>
    api.get(`/organizations/${orgId}/leads${status ? `?status=${status}` : ''}`),
  createLead: (orgId, data, userId, userName) =>
    api.post(`/organizations/${orgId}/leads?user_id=${userId}&user_name=${encodeURIComponent(userName)}`, data),
  getLead: (orgId, leadId) =>
    api.get(`/organizations/${orgId}/leads/${leadId}`),
  updateLead: (orgId, leadId, data, userId, userName) =>
    api.put(`/organizations/${orgId}/leads/${leadId}?user_id=${userId}&user_name=${encodeURIComponent(userName)}`, data),
  deleteLead: (orgId, leadId, userId) =>
    api.delete(`/organizations/${orgId}/leads/${leadId}?user_id=${userId}`),
  convertLead: (orgId, leadId, data, userId, userName) =>
    api.post(`/organizations/${orgId}/leads/${leadId}/convert?user_id=${userId}&user_name=${encodeURIComponent(userName)}`, data),
  getLeadStats: (orgId) =>
    api.get(`/organizations/${orgId}/leads/stats/summary`),

  // Follow-Up System
  getFollowUpSettings: (orgId) => api.get(`/organizations/${orgId}/follow-up-settings`),
  updateFollowUpSettings: (orgId, data, userId) =>
    api.put(`/organizations/${orgId}/follow-up-settings?user_id=${userId}`, data),
  getEstimateFollowUps: (orgId, estimateId) =>
    api.get(`/organizations/${orgId}/estimates/${estimateId}/follow-ups`),
  pauseFollowUps: (orgId, estimateId, userId) =>
    api.post(`/organizations/${orgId}/estimates/${estimateId}/pause-follow-ups?user_id=${userId}`),
  resumeFollowUps: (orgId, estimateId, userId) =>
    api.post(`/organizations/${orgId}/estimates/${estimateId}/resume-follow-ups?user_id=${userId}`),
  sendManualFollowUp: (orgId, estimateId, userId, userName) =>
    api.post(`/organizations/${orgId}/estimates/${estimateId}/send-follow-up?user_id=${userId}&user_name=${encodeURIComponent(userName)}`),
  
  // Public Estimate
  getPublicEstimate: (token) => api.get(`/public/estimate/${token}`),
  approvePublicEstimate: (token, clientName, termsAccepted = false) => 
    api.post(`/public/estimate/${token}/approve?client_name=${encodeURIComponent(clientName)}&terms_accepted=${termsAccepted}`),
  declineEstimate: (token, reason) => 
    api.post(`/public/estimate/${token}/decline${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`),
  
  // Invoices
  getInvoices: (orgId, status = '', clientId = '') => {
    let url = `/organizations/${orgId}/invoices`;
    const params = [];
    if (status) params.push(`status=${status}`);
    if (clientId) params.push(`client_id=${clientId}`);
    if (params.length) url += '?' + params.join('&');
    return api.get(url);
  },
  getInvoice: (orgId, invoiceId) => api.get(`/organizations/${orgId}/invoices/${invoiceId}`),
  createInvoice: (orgId, data, userId, userName) => 
    api.post(`/organizations/${orgId}/invoices?user_id=${userId}&user_name=${encodeURIComponent(userName)}`, data),
  convertToInvoice: (orgId, estimateId, userId, userName) => 
    api.post(`/organizations/${orgId}/estimates/${estimateId}/convert-to-invoice?user_id=${userId}&user_name=${encodeURIComponent(userName)}`),
  recordPayment: (orgId, invoiceId, amount, method, notes, userId, userName, paymentDate = null) => 
    api.post(`/organizations/${orgId}/invoices/${invoiceId}/record-payment?amount=${amount}&method=${method}${notes ? `&notes=${encodeURIComponent(notes)}` : ''}${paymentDate ? `&payment_date=${paymentDate}` : ''}&user_id=${userId}&user_name=${encodeURIComponent(userName)}`),
  deleteInvoice: (orgId, invoiceId, userId) => 
    api.delete(`/organizations/${orgId}/invoices/${invoiceId}?user_id=${userId}`),
  getInvoicePdf: (orgId, invoiceId) => 
    api.get(`/organizations/${orgId}/invoices/${invoiceId}/pdf`, { responseType: 'blob' }),
  
  // Site Visits
  getSiteVisits: (orgId, filters = {}) => {
    let url = `/organizations/${orgId}/site-visits`;
    const params = [];
    if (filters.status) params.push(`status=${filters.status}`);
    if (filters.clientId) params.push(`client_id=${filters.clientId}`);
    if (filters.assignedEstimatorId) params.push(`assigned_estimator_id=${filters.assignedEstimatorId}`);
    if (params.length) url += '?' + params.join('&');
    return api.get(url);
  },
  getSiteVisit: (orgId, visitId) => api.get(`/organizations/${orgId}/site-visits/${visitId}`),
  createSiteVisit: (orgId, data, userId, userName) => 
    api.post(`/organizations/${orgId}/site-visits?user_id=${userId}&user_name=${encodeURIComponent(userName)}`, data),
  updateSiteVisit: (orgId, visitId, data, userId, userName) => 
    api.put(`/organizations/${orgId}/site-visits/${visitId}?user_id=${userId}&user_name=${encodeURIComponent(userName)}`, data),
  deleteSiteVisit: (orgId, visitId, userId) => 
    api.delete(`/organizations/${orgId}/site-visits/${visitId}?user_id=${userId}`),
  convertSiteVisitToEstimate: (orgId, visitId, userId, userName) => 
    api.post(`/organizations/${orgId}/site-visits/${visitId}/convert-to-estimate?user_id=${userId}&user_name=${encodeURIComponent(userName)}`),
  resetSiteVisitConversion: (orgId, visitId, userId, userName, force = false) => 
    api.post(`/organizations/${orgId}/site-visits/${visitId}/reset-conversion?user_id=${userId}&user_name=${encodeURIComponent(userName)}&force=${force}`),
  completeSiteVisit: (orgId, visitId, userId, userName, force = false) =>
    api.post(`/organizations/${orgId}/site-visits/${visitId}/complete?user_id=${userId || ''}&user_name=${encodeURIComponent(userName || '')}&force=${force}`),
  getSiteVisitSnapshots: (orgId, visitId) =>
    api.get(`/organizations/${orgId}/site-visits/${visitId}/snapshots`),
  getPendingReviewVisits: (orgId) => 
    api.get(`/organizations/${orgId}/site-visits/pending-review`),

  // Recurring Jobs
  getRecurringJobs: (orgId) =>
    api.get(`/organizations/${orgId}/recurring-jobs`),
  generateRecurringOccurrences: (orgId, jobId, weeksAhead = 4) =>
    api.post(`/organizations/${orgId}/recurring-jobs/${jobId}/generate?weeks_ahead=${weeksAhead}`),
  stopRecurringJob: (orgId, jobId, userId, userName) =>
    api.put(`/organizations/${orgId}/recurring-jobs/${jobId}/stop?user_id=${userId || ''}&user_name=${encodeURIComponent(userName || '')}`),

  // Jobs
  getJobs: (orgId, filters = {}) => {
    let url = `/organizations/${orgId}/jobs`;
    const params = [];
    if (filters.clientId) params.push(`client_id=${filters.clientId}`);
    if (filters.status) params.push(`status=${filters.status}`);
    if (filters.startDate) params.push(`start_date=${filters.startDate}`);
    if (filters.endDate) params.push(`end_date=${filters.endDate}`);
    if (params.length) url += '?' + params.join('&');
    return api.get(url);
  },
  getJob: (orgId, jobId) => api.get(`/organizations/${orgId}/jobs/${jobId}`),
  createJob: (orgId, data, userId, userName) => 
    api.post(`/organizations/${orgId}/jobs?user_id=${userId}&user_name=${encodeURIComponent(userName)}`, data),
  updateJob: (orgId, jobId, data, userId, userName) => 
    api.put(`/organizations/${orgId}/jobs/${jobId}?user_id=${userId}&user_name=${encodeURIComponent(userName)}`, data),
  deleteJob: (orgId, jobId, userId) => 
    api.delete(`/organizations/${orgId}/jobs/${jobId}?user_id=${userId}`),
  createJobFromEstimate: (orgId, estimateId, startDate, userId, userName) => 
    api.post(`/organizations/${orgId}/jobs/from-estimate/${estimateId}?start_date=${startDate}&user_id=${userId}&user_name=${encodeURIComponent(userName)}`),
  getJobIcal: (orgId, jobId) => 
    api.get(`/organizations/${orgId}/jobs/${jobId}/ical`, { responseType: 'blob' }),
  getJobPdf: (orgId, jobId) => 
    api.get(`/organizations/${orgId}/jobs/${jobId}/pdf`, { responseType: 'blob' }),
  
  // Activity
  getActivity: (orgId, limit = 50, role = '') => api.get(`/organizations/${orgId}/activity?limit=${limit}${role ? `&role=${role}` : ''}`),
  
  // Stats
  getStats: (orgId) => api.get(`/organizations/${orgId}/stats`),
  
  // Export
  exportClients: (orgId) => api.get(`/organizations/${orgId}/export/clients`, { responseType: 'blob' }),
  exportEstimates: (orgId) => api.get(`/organizations/${orgId}/export/estimates`, { responseType: 'blob' }),
  exportInvoices: (orgId) => api.get(`/organizations/${orgId}/export/invoices`, { responseType: 'blob' }),
  
  // Invoice Update (with revision tracking)
  updateInvoice: (orgId, invoiceId, data, userId, userName) =>
    api.put(`/organizations/${orgId}/invoices/${invoiceId}?user_id=${userId}&user_name=${encodeURIComponent(userName)}`, data),
  
  // Estimate to Job Conversion
  convertEstimateToJob: (estimateId, orgId, startDate) =>
    api.post(`/estimates/${estimateId}/convert-to-job?org_id=${orgId}${startDate ? `&start_date=${startDate}` : ''}`),
  
  // Public Invoice
  getPublicInvoice: (token) => api.get(`/public/invoice/${token}`),
  
  // Photo Upload
  uploadPhoto: (orgId, entityType, entityId, file, userId, userName, caption = '', photoType = 'general') => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(
      `/organizations/${orgId}/photos/upload?entity_type=${entityType}&entity_id=${entityId}&user_id=${userId}&user_name=${encodeURIComponent(userName || '')}&caption=${encodeURIComponent(caption)}&photo_type=${photoType}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },
  getEntityPhotos: (orgId, entityType, entityId) => 
    api.get(`/organizations/${orgId}/${entityType}/${entityId}/photos`),
  deletePhoto: (orgId, photoId, userId) => 
    api.delete(`/organizations/${orgId}/photos/${photoId}?user_id=${userId}`),
};

// Super Admin API helpers
export const adminApi = {
  login: (username, password) => api.post('/admin/login', { username, password }),
  logout: (token) => api.post(`/admin/logout?token=${token}`),
  
  // Organizations
  getOrganizations: (token) => api.get(`/admin/organizations?admin_token=${token}`),
  getOrganization: (orgId, token) => api.get(`/admin/organizations/${orgId}?admin_token=${token}`),
  createOrganization: (data, token) => api.post(`/admin/organizations?admin_token=${token}`, data),
  updateOrganization: (orgId, data, token) => api.put(`/admin/organizations/${orgId}?admin_token=${token}`, data),
  archiveOrganization: (orgId, token) => api.put(`/admin/organizations/${orgId}/archive?admin_token=${token}`),
  restoreOrganization: (orgId, token) => api.put(`/admin/organizations/${orgId}/restore?admin_token=${token}`),
  deleteOrganization: (orgId, token, permanent = true) => api.delete(`/admin/organizations/${orgId}?admin_token=${token}&permanent=${permanent}`),
  getOrgAuditTrail: (orgId, token, limit = 100) => api.get(`/admin/organizations/${orgId}/audit-trail?admin_token=${token}&limit=${limit}`),
  
  // Users
  getUsers: (orgId, token) => api.get(`/admin/users/${orgId}?admin_token=${token}`),
  disableUser: (userId, token) => api.put(`/admin/users/${userId}/disable?admin_token=${token}`),
  enableUser: (userId, token) => api.put(`/admin/users/${userId}/enable?admin_token=${token}`),
  resetPassword: (userId, token, newPassword = null) => 
    api.put(`/admin/users/${userId}/reset-password?admin_token=${token}${newPassword ? `&new_password=${newPassword}` : ''}`),
  sendWelcomeEmail: (userId, token) => api.post(`/admin/users/${userId}/send-welcome-email?admin_token=${token}`),
  
  // Impersonation
  impersonateUser: (userId, token) => api.post(`/admin/impersonate/${userId}?admin_token=${token}`),
  
  // Stats
  getGlobalStats: (token) => api.get(`/admin/stats?admin_token=${token}`),
  
  // Platform Settings
  getPlatformSettings: (token) => api.get(`/admin/platform-settings?admin_token=${token}`),
  updatePlatformSettings: (token, settings) => api.put(`/admin/platform-settings?admin_token=${token}`, settings),
  
  // Admin Invoices (BirdenMedia -> Clients)
  getAdminInvoices: (token, orgId = null) => api.get(`/admin/invoices?admin_token=${token}${orgId ? `&org_id=${orgId}` : ''}`),
  getAdminInvoice: (invoiceId, token) => api.get(`/admin/invoices/${invoiceId}?admin_token=${token}`),
  createAdminInvoice: (token, data) => api.post(`/admin/invoices?admin_token=${token}`, data),
  updateAdminInvoice: (invoiceId, token, data) => api.put(`/admin/invoices/${invoiceId}?admin_token=${token}`, data),
  deleteAdminInvoice: (invoiceId, token) => api.delete(`/admin/invoices/${invoiceId}?admin_token=${token}`),
  sendAdminInvoice: (invoiceId, token) => api.post(`/admin/invoices/${invoiceId}/send?admin_token=${token}`),
  markAdminInvoicePaid: (invoiceId, token, data) => api.post(`/admin/invoices/${invoiceId}/mark-paid?admin_token=${token}`, data),
  getAdminInvoicePaymentLink: (invoiceId, token) => api.get(`/admin/invoices/${invoiceId}/payment-link?admin_token=${token}`),
  getOrgBillingSummary: (orgId, token) => api.get(`/admin/organizations/${orgId}/billing?admin_token=${token}`),
  
  // Email Simulator
  getEmailSimulatorData: (token) => api.get(`/admin/email-simulator/personas?admin_token=${token}`),
  generateClientEmails: (token, data) => api.post(`/admin/email-simulator/generate?admin_token=${token}`, data),
  sendSimulatedEmail: (token, data) => api.post(`/admin/email-simulator/send?admin_token=${token}`, data),
  scheduleSimulatedEmails: (token, data) => api.post(`/admin/email-simulator/schedule?admin_token=${token}`, data),
  getScheduledEmails: (token) => api.get(`/admin/email-simulator/scheduled?admin_token=${token}`),
  clearScheduledEmails: (token) => api.delete(`/admin/email-simulator/scheduled?admin_token=${token}`),
  
  // Admin Estimate Overrides
  getOrgEstimates: (orgId, token, status = '') => 
    api.get(`/admin/organizations/${orgId}/estimates?admin_token=${token}${status ? `&status=${status}` : ''}`),
  overrideEstimate: (orgId, estimateId, data, token) => 
    api.put(`/admin/organizations/${orgId}/estimates/${estimateId}/override?admin_token=${token}`, data),
  getEstimateAuditHistory: (orgId, estimateId, token) => 
    api.get(`/admin/organizations/${orgId}/estimates/${estimateId}/audit-history?admin_token=${token}`),
};
